import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface ContentResponse {
  id: string;
  content_id: string;
  translation_group_id?: string | null;
  concept_id: string | null;
  app_id: string;
  language: string;
  title: string;
  content: string;
  image_path: string | null;
  tags: string[];
  model: string;
  is_approved: boolean;
  is_premium: boolean;
  source: string;
  status?: 'draft' | 'published';
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateContentRequest {
  concept_id?: string | null;
  app_id: string;
  language: string;
  target_languages?: string[] | null;
  title: string;
  content: string;
  image_path?: string | null;
  tags?: string[] | null;
  model: string;
}

export interface EditContentRequest {
  title?: string | null;
  content?: string | null;
  image_path?: string | null;
  tags?: string[] | null;
}

export interface ApproveContentRequest {
  is_approved: boolean;
}

export interface UpdateContentStatusRequest {
  status: 'draft' | 'published';
}

export interface ListContentsParams {
  concept_id?: string | null;
  app_id?: string | null;
  language?: string | null;
  is_approved?: boolean | null;
  is_premium?: boolean | null;
  status?: string | null;
  search?: string | null;
  /** created_at sıralaması: 'newest_first' (varsayılan) veya 'oldest_first' */
  sort_order?: 'newest_first' | 'oldest_first' | null;
  skip?: number;
  limit?: number;
}

export interface ListContentsResult {
  items: ContentResponse[];
  /** Number of items in the paginated page (before sibling expansion). Used for hasNext logic. */
  baseCount: number;
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class ContentService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/contents/`;

  list(params?: ListContentsParams): Observable<ListContentsResult> {
    let p = new HttpParams();
    if (params?.concept_id) p = p.set('concept_id', params.concept_id);
    if (params?.app_id) p = p.set('app_id', params.app_id);
    if (params?.language) p = p.set('language', params.language);
    if (params?.is_approved != null) p = p.set('is_approved', String(params.is_approved));
    if (params?.is_premium != null) p = p.set('is_premium', String(params.is_premium));
    if (params?.status) p = p.set('status', params.status);
    if (params?.search) p = p.set('search', params.search);
    if (params?.sort_order === 'oldest_first') p = p.set('sort_order', 'oldest_first');
    if (params?.skip != null) p = p.set('skip', String(params.skip));
    if (params?.limit != null) p = p.set('limit', String(params.limit));
    return this.http.get<ContentResponse[]>(this.baseUrl, { params: p, observe: 'response' }).pipe(
      map(response => ({
        items: response.body ?? [],
        baseCount: parseInt(response.headers.get('X-Base-Count') ?? '0', 10) || (response.body?.length ?? 0),
      }))
    );
  }

  get(content_id: string): Observable<ContentResponse> {
    return this.http.get<ContentResponse>(`${this.baseUrl}${content_id}`);
  }

  create(payload: CreateContentRequest): Observable<ContentResponse> {
    return this.http.post<ContentResponse>(this.baseUrl, payload);
  }

  edit(content_id: string, payload: EditContentRequest): Observable<ContentResponse> {
    return this.http.put<ContentResponse>(`${this.baseUrl}${content_id}`, payload);
  }

  delete(content_id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${content_id}`);
  }

  bulkDelete(contentIds: string[]): Observable<{ deleted: number }> {
    return this.http.delete<{ deleted: number }>(`${this.baseUrl}bulk`, {
      body: { content_ids: contentIds },
    });
  }

  approve(content_id: string, payload: ApproveContentRequest): Observable<ContentResponse> {
    return this.http.patch<ContentResponse>(`${this.baseUrl}${content_id}/approve`, payload);
  }

  updateStatus(content_id: string, payload: UpdateContentStatusRequest): Observable<ContentResponse> {
    return this.http.patch<ContentResponse>(`${this.baseUrl}${content_id}/status`, payload);
  }
}
